
import { Injectable } from '@angular/core';
import { ActivatedRoute, Route, Router } from '@angular/router';
import { IBaseModuleComponent } from '@base/component/IBaseComponent';
import { AppConfigService } from '@configuration/app.config.service';
import { Utils } from '../helpers/Utils';
import { ScreenService } from './screen.service';
import { SessionService } from './session.service';
import { SysEventsService } from './sysevents.service';
import { SysGlobals } from './sysglobals';

/*
The idea of navigation process is as follows:
1. Get the segments from the function params. Then remove all prefixes (randomizers and module name)
2. Get the target page and find the route in the "clean" routes (already prepared, without any prefixes)
3. Figure out is that route is a window or not
4. Add query params to the route
5. Figure out the module name by route component
6. Add page randomizer prefix and module name to the route
7. Navigate to the page or window
*/

export const M4URouteRandomizers: string[] =
    [
        'X_',
        'Y_',
        'Z_',
        'W_',
        'T_'
    ];

@Injectable()
export class NavigatorService {
    constructor(
        private router: Router,
        private activatedRoute: ActivatedRoute,
        private sysEvents: SysEventsService,
        private appConfigService: AppConfigService,
        private sessionService: SessionService,
        private screenService: ScreenService) { }

    public IsCurrentRouteWindow(routeUrl?: string) {
        const windowIndex = window.location.pathname.indexOf('window:');
        return windowIndex > -1 || (routeUrl && routeUrl.indexOf('window:') > -1);
    }

    //Return custom data attached to this route
    public GetCustomRouteData() {
        return this.activatedRoute.snapshot.data;
    }

    public GetCurrentRouteWithoutParams() {

        let route = this.activatedRoute;
        let url = '';

        // get full route like '/pages/general/notification-template'
        while (route?.snapshot?.url.length > 0) {
            url = `/${route.snapshot.url[0].path}${url}`;
            route = route.parent;
        }

        return url;
    }

    //Return current query params
    private GetQueryParamsInternal(): any {
        const params = this.activatedRoute.snapshot.queryParams;
        const newParams = {};

        for (const [key, value] of Object.entries(params)) {
            newParams[key] = this.getParamValue(value);
        }

        return newParams;
    }

    //Return query params that are relevant for the route, renamed back to their original names
    public GetQueryParams(bIsWindow: boolean): any {
        const existingParams = this.GetQueryParamsInternal();

        //Remove prefixes and copy to new object
        return this.GetQueryParamsWithoutPrefixes(existingParams, bIsWindow);
    }

    public GetQueryParamsWithoutPrefixes(params: any, bIsWindow: boolean): any {
        const sPrefix = this.GetQueryParamsPrefix(bIsWindow);
        const newParams = {};
        for (const key in params) {
            let paramKey = key;
            if (key.startsWith(sPrefix)) paramKey = key.substring(sPrefix.length);
            const value = params[key] === 'false' ? false : params[key] === 'true' ? true : params[key];
            Object.assign(newParams, { [paramKey]: value });
        }

        return newParams;
    }

    public ClearQueryParams() {
        this.activatedRoute.snapshot.queryParams = {};
    }

    //Return the prefix string for query params for the route
    private GetQueryParamsPrefix(bIsWindow: boolean): string {
        return bIsWindow ? 'W_' : 'P_';
    }


    //Return the params for the route
    public GetRouteParams(): any {
        const params = this.activatedRoute.snapshot.params;
        return Object.keys(params).length ? params : this.GetQueryParams(this.IsCurrentRouteWindow());
    }

    //Return current route
    //use window since activatedRoute is not always correct, due to DI being on the component level, 
    //and some components are not related to route
    public GetCurrentRoute(): string {
        const sLocation: string = `${window.location.pathname}${window.location.search}`;
        return this.FixDeploymentLocation(sLocation);
    }

    //Main navigation function
    //Special value - params will be null to signal closing the window route
    public Navigate(params: any[], optionalParams?: any, ModuleName?: any) {

        this.sessionService.RefreshExpiredSessionTimeout();

        const urlTree = this.GetUrlTree(params, optionalParams, ModuleName);
        return this.router.navigateByUrl(urlTree);

    }

    public NavigateByUrl(url: string) {
        this.sessionService.RefreshExpiredSessionTimeout();

        return this.router.navigateByUrl(url);
    }

    private GetUrlTree(params: any[], optionalParams?: any, ModuleName?: any) {
        //Start as an array
        let segments: string[];
        if (typeof params === 'string') segments.push(params); else segments = params;

        if (segments && !segments.some(x => x !== '')) return;

        //Clean, and put all segments in onc nice string array
        let segmentsClean: string[] = [];
        let sTargetPage: string;
        if (segments != null) {
            segments.forEach(sSegment => {
                sSegment = sSegment && sSegment.toString();
                if (sSegment) {
                    sSegment.split('/').forEach(sPart => {
                        if (sPart != null && sPart != '') {
                            sPart = sPart.replace('(', '').replace(')', '');

                            //Remove randomizers from route path
                            const sPrefix = M4URouteRandomizers.find(item => sPart.startsWith(item));
                            if (sPrefix) sPart = sPart.substring(sPrefix.length);

                            //Remove module prefix from route path
                            const modulePrefix = this.appConfigService.AppConfig.ModuleName.split(',').find(x => sPart.startsWith(x));
                            if (modulePrefix) sPart = sPart.substring(modulePrefix.length + 1);

                            segmentsClean.push(sPart);
                        }
                    });
                }
            });

            //Set target page we are navigating to
            const generalIndex = segmentsClean.indexOf('general');
            sTargetPage = generalIndex >= 0 ? segmentsClean[2] : segmentsClean[1];
        } else {
            segmentsClean = null;
            sTargetPage = null;
        }

        //Find the relevant route by the 2nd segment - match it with the route first segment
        //Check the router outlet to determine if its a window
        let bIsWindow: boolean;
        let route;
        if (sTargetPage != null) {
            //Get route by target page
            const cleanRoutes = this.GetCleanRoutes(this.router.config);
            const targetRoutes = cleanRoutes.filter(x => x.path.split('/')[0] == sTargetPage);
            if (targetRoutes.length > 1) {
                route = optionalParams && Object.keys(optionalParams).some(x => x == 'ShowAsWindow') && optionalParams['ShowAsWindow']
                    ? targetRoutes.find(x => x.outlet == 'window')
                    : targetRoutes.find(x => !x.outlet);
            } else if (targetRoutes.length === 1) {
                route = targetRoutes[0];
            }
            bIsWindow = (route != undefined && route.outlet == 'window' ? true : false);
        } else {
            bIsWindow = true;
        }

        //Rename query params that we need to pass according to the route we use - window/primary
        //Build a new structure in order not to modify the passed in param
        const sPrefix = this.GetQueryParamsPrefix(bIsWindow);
        const newParams = {};
        for (const key in optionalParams) {
            if (key === 'ShowAsWindow') continue;
            Object.assign(newParams, { [sPrefix + key]: this.getParam(optionalParams[key]) });
        }

        //Add query params from existing route so that we get a combined list
        if (optionalParams == undefined) optionalParams = {};

        //Get module name if not specified, from the current route
        if (ModuleName == undefined) {
            const routeComponent = (route ? route.component : this.activatedRoute.snapshot.component) as any as IBaseModuleComponent;
            ModuleName = routeComponent && routeComponent.ModuleName ? `${routeComponent.ModuleName}_` : '';
        } else {
            ModuleName = ModuleName + '_';
        }

        //Remove pages, and set the prefix
        let routePrefix = '';
        if (segmentsClean != null) {
            const pagesIndex = segmentsClean.indexOf('pages');
            if (pagesIndex >= 0) {
                routePrefix = segmentsClean.shift();
            }
            //Add general to primary outlet
            const generalIndex = segmentsClean.indexOf('general');
            if (generalIndex >= 0) {
                routePrefix = `${routePrefix}/${segmentsClean.shift()}`;
            }
            //Add randomizer and module name prefixes to the page route
            if (pagesIndex >= 0) {
                segmentsClean[0] = this.GetPagesPrefix() + ModuleName + segmentsClean[0];
            }
        } else {
            const currentRoute = this.GetCurrentRoute();
            const segments = currentRoute.replace(/^\/|\/$/g, '').split('/');

            routePrefix = segments.shift();
            //Add general to primary outlet
            if (segments.indexOf('general') >= 0) {
                routePrefix = `${routePrefix}/${segments.shift()}`;
            }
        }
        const windowPrimaryOutlet = [];
        let routeSnapshot = this.router.routerState.snapshot.root;
        while (routeSnapshot.firstChild) {
            routeSnapshot = routeSnapshot.firstChild;
            windowPrimaryOutlet.push(routeSnapshot.url.map(x => x.path).join('/'));
        }

        const primaryOutlet = bIsWindow ? windowPrimaryOutlet.join('/').replace(`${routePrefix}/`, '') : segmentsClean;
        const windowOutlet = bIsWindow ? segmentsClean : null;
        return this.router.createUrlTree([routePrefix, { outlets: { primary: primaryOutlet, window: windowOutlet } }],
            { queryParams: newParams });
    }

    FixDeploymentLocation(sURL: string): string {
        const sPath : string = this.appConfigService.M4UConfig.deploymentLocation;
        if(!sURL || sPath == null) return sURL;
        const regex = new RegExp(sPath, 'i');
        return sURL.replace(regex, '');
    }


    //Get routes from config without randomizer prefixes and module names
    public GetCleanRoutes(config: Route[]): Route[] {
        let routes = [];
        for (let i = 0; i < config.length; i++) {
            //Copy route object to avoid modifying real route config
            const route = { ...config[i] };
            //Remove randomizer prefix from route path
            M4URouteRandomizers.forEach(x => {
                if (route.path.startsWith(x)) route.path = route.path.replace(x, '');
            });
            //Remove module name prefix from route path
            this.appConfigService.AppConfig.ModuleName.split(',').forEach(x => {
                if (route.path.startsWith(x)) route.path = route.path.replace(`${x}_`, '');
            });
            //Add to "clean" routes collection
            routes.push(route);
            if (route.children) {
                //Get "clean" route children
                const childrenRoutes = this.GetCleanRoutes(route.children);
                //Add "clean" route children to "clean" routes collection
                routes = [...routes, ...childrenRoutes];
            }
        }
        //Remove duplicates from "clean" routes collection that appeared because of randomizers prefixes
        routes = routes.filter((value, index, self) => self.map(x => {
            return { path: x.path, outlet: x.outlet };
        }).findIndex(x => x.path === value.path && x.outlet === value.outlet) === index);
        return routes;
    }

    //Switch routes to allow same page navigation
    private GetPagesPrefix(): string {
        SysGlobals.NavigationSequence = SysGlobals.NavigationSequence == undefined ? 1 : SysGlobals.NavigationSequence + 1;
        return M4URouteRandomizers[SysGlobals.NavigationSequence % M4URouteRandomizers.length];
    }

    //Send an event to force reload of component
    //For now, this should not be used, as same route navigation works fine
    public ReloadComponent(sComponentName: string) {
        this.sysEvents.OnReloadComponent.emit(sComponentName);
    }


    //Reload current page
    public ReloadApplication() {
        this.router.navigateByUrl(this.GetCurrentRoute());
    }

    //Go to previous page - close the window
    public NavigateBack() {
        return this.Navigate(null);
    }

    public GetUrl(route: string, queryParams: any) {
        const params = {};
        const sPrefix = this.GetQueryParamsPrefix(false);
        Object.keys(queryParams).forEach(key => {
            Object.assign(params, { [sPrefix + key]: this.getParam(queryParams[key]) });
        });

        const baseUrl = window.location.href.replace(this.router.url, '');
        const urlTree = this.GetUrlTree([route], queryParams);
        const url = this.router.serializeUrl(urlTree);
        return `${baseUrl}${url}`;
    }

    //Load external URL in a separate form
    public NavigateExternal(sURL: string, bNewWindow: boolean = true) {
        if (bNewWindow) {
            window.open(sURL, '_blank');
        } else {
            window.location.href = sURL;
        }
    }

    public NavigatePhone(sPhone: string) {
        this.NavigateExternal(`tel:${sPhone}`);
    }

    public NavigateEmail(sEmail: string) {
        this.NavigateExternal(`mailto:${sEmail}`);
    }

    public NavigateGoogle(sQuery: string) {
        this.NavigateExternal(`https://www.google.com/search?q=${sQuery}`);
    }

    public NavigateWhatsApp(sPhone: string) {
        const sClean = sPhone.replace('-', '').replace(' ', '').replace('.', '');
        this.NavigateExternal(`https://api.whatsapp.com/send?phone=972${sClean}`);
    }

    public NavigateWaze(sAddress: string) {
        if (this.screenService.IsMobile()) {
            this.NavigateExternal(`waze://?q=${sAddress}`);
        }

        this.NavigateExternal(`https://waze.com/ul?q=${sAddress}&navigate=yes`);
    }

    public IsWhatsAppPhone(sPhone: string): boolean {
        return (!Utils.IsEmpty(sPhone) && sPhone.startsWith('05'));
    }

    private getParam(param: any): any {
        if (Utils.IsEmpty(param) || Utils.IsEmpty(param.getFullYear)) return param;

        const date = param as Date;

        return date.toISOString();
    }

    private getParamValue(param: any): any {
        if (Utils.IsEmpty(param)) return param;
        if (Array.isArray(param) || (typeof param === 'string' && !/^[0-9]{4}-[0-9]{1,2}-[0-9]{1,2}$/.test(param))) return param;

        const parts = (param as string).split('-');
        return new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
    }
}

export function PrepareRoutes(routes: Route[]): Route[] {
    const result: Route[] = [];
    //Copy route object to avoid modifying real routes
    const oRoutes: Route[] = [...routes];
    //Add randomizer prefixes to the routes
    M4URouteRandomizers.forEach((oRandomizer) => oRoutes.forEach(oRoute => result.push(GetRouteWithPrefix(oRoute, oRandomizer))));
    return result;
}

export function GetRoutesWithPrefix(routes: Route[], prefix: string): Route[] {
    const result: Route[] = [];
    //Copy route object to avoid modifying real routes
    const oRoutes: Route[] = [...routes];

    //Add any prefix to the routes
    oRoutes.forEach(oRoute => result.push(GetRouteWithPrefix(oRoute, prefix)));
    return result;
}

//Add any prefix to the single route
export function GetRouteWithPrefix(oRoute: Route, prefix: string): Route {
    //Copy route object to avoid modifying real route
    const preparedRoute = { ...oRoute };
    //Add prefix to the route path
    preparedRoute.path = prefix + preparedRoute.path;
    return preparedRoute;
}
